import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "@/components/seo/SEO";
import { GoldButton, GhostButton, Eyebrow, SectionHeading, AmbientOrbs, GlassCard } from "@/components/common/ui";
import { Check, Minus } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const TIERS_EN = [
  {
    key: "starter",
    name: "Starter",
    tagline: "For boutique properties finding their rhythm.",
    ideal: "1 property · up to 50 rooms",
    highlights: [
      "Reception + Housekeeping + Restaurant",
      "Your hotel knowledge base",
      "The AI colleague (fair-use)",
      "Business email · 5 mailboxes included",
      "Guest communication (email + WhatsApp)",
      "Guest app · standard theme",
      "Standard integrations",
      "Support · one business day",
    ],
  },
  {
    key: "professional",
    name: "Professional",
    tagline: "For established hotels ready to run on one platform.",
    ideal: "1-5 properties · up to 500 rooms",
    popular: true,
    highlights: [
      "Everything in Starter, plus:",
      "Maintenance · Marketing · Management dashboard",
      "Internal communication + tasks",
      "Business email · unlimited mailboxes",
      "Guest app · branded (your logo, your colours)",
      "Website & CMS module",
      "Priority integrations (Mews, Apaleo, Stripe, Booking.com)",
      "Priority support · same business day",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    tagline: "For chains, resorts and destinations that scale.",
    ideal: "Multi-property · multi-brand · unlimited rooms",
    highlights: [
      "Everything in Professional, plus:",
      "Portfolio dashboards & cross-property analytics",
      "SSO/SAML · SCIM · RBAC",
      "Custom integrations & Open APIs",
      "Data residency (EU · US · APAC)",
      "Dedicated hotelier account team",
      "99.99% uptime SLA · 15-min security response",
      "Onboarding & training programme",
    ],
  },
];

const TIERS_NL = [
  {
    key: "starter",
    name: "Starter",
    tagline: "Voor boutique-hotels die hun ritme vinden.",
    ideal: "1 property · tot 50 kamers",
    highlights: [
      "Receptie + Housekeeping + Restaurant",
      "Uw hotel-kennisbank",
      "De AI-collega (fair-use)",
      "Zakelijke e-mail · 5 mailboxen inbegrepen",
      "Gastcommunicatie (e-mail + WhatsApp)",
      "Gast-app · standaardthema",
      "Standaard integraties",
      "Support · binnen één werkdag",
    ],
  },
  {
    key: "professional",
    name: "Professional",
    tagline: "Voor gevestigde hotels die op één platform willen draaien.",
    ideal: "1-5 properties · tot 500 kamers",
    popular: true,
    highlights: [
      "Alles uit Starter, plus:",
      "Onderhoud · Marketing · Management-dashboard",
      "Interne communicatie + taken",
      "Zakelijke e-mail · onbeperkte mailboxen",
      "Gast-app · branded (uw logo, uw kleuren)",
      "Website & CMS module",
      "Prioriteit-integraties (Mews, Apaleo, Stripe, Booking.com)",
      "Priority-support · dezelfde werkdag",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    tagline: "Voor ketens, resorts en bestemmingen die schalen.",
    ideal: "Multi-property · multi-brand · onbeperkte kamers",
    highlights: [
      "Alles uit Professional, plus:",
      "Portfolio-dashboards & cross-property analytics",
      "SSO/SAML · SCIM · RBAC",
      "Custom integraties & Open API's",
      "Data-residency (EU · VS · APAC)",
      "Toegewijd hotelier account-team",
      "99,99% uptime SLA · 15-minuten security response",
      "Onboarding- & trainingsprogramma",
    ],
  },
];

// Feature matrix — rows: category / feature / starter / pro / enterprise
const MATRIX = {
  en: [
    { section: "Daily workflows", rows: [
      ["Reception", true, true, true],
      ["Housekeeping", true, true, true],
      ["Restaurant & F&B", true, true, true],
      ["Maintenance", false, true, true],
      ["Marketing", false, true, true],
      ["Management dashboard", "Basic", true, "Advanced"],
    ]},
    { section: "Communication", rows: [
      ["Guest communication (email + WhatsApp)", true, true, true],
      ["Internal communication & tasks", false, true, true],
      ["Business email", "5 mailboxes", "Unlimited", "Unlimited"],
    ]},
    { section: "Guest-facing", rows: [
      ["Guest app", "Standard", "Branded", "Branded + custom"],
      ["Website & CMS", false, true, true],
    ]},
    { section: "AI & knowledge", rows: [
      ["Hotel knowledge base", true, true, true],
      ["AI colleague", "Fair-use", "Included", "Custom limits"],
      ["Bring-your-own model keys", false, false, true],
    ]},
    { section: "Enterprise", rows: [
      ["SSO / SAML / SCIM / RBAC", false, false, true],
      ["Data residency (EU / US / APAC)", false, false, true],
      ["Uptime SLA", "99.9%", "99.95%", "99.99%"],
      ["Support", "1 business day", "Same business day", "Dedicated account team"],
    ]},
  ],
  nl: [
    { section: "Dagelijkse workflows", rows: [
      ["Receptie", true, true, true],
      ["Housekeeping", true, true, true],
      ["Restaurant & F&B", true, true, true],
      ["Onderhoud", false, true, true],
      ["Marketing", false, true, true],
      ["Management-dashboard", "Basis", true, "Uitgebreid"],
    ]},
    { section: "Communicatie", rows: [
      ["Gastcommunicatie (e-mail + WhatsApp)", true, true, true],
      ["Interne communicatie & taken", false, true, true],
      ["Zakelijke e-mail", "5 mailboxen", "Onbeperkt", "Onbeperkt"],
    ]},
    { section: "Gast-facing", rows: [
      ["Gast-app", "Standaard", "Branded", "Branded + custom"],
      ["Website & CMS", false, true, true],
    ]},
    { section: "AI & kennis", rows: [
      ["Hotel-kennisbank", true, true, true],
      ["AI-collega", "Fair-use", "Inbegrepen", "Custom limieten"],
      ["Eigen model-keys meenemen", false, false, true],
    ]},
    { section: "Enterprise", rows: [
      ["SSO / SAML / SCIM / RBAC", false, false, true],
      ["Data-residency (EU / VS / APAC)", false, false, true],
      ["Uptime SLA", "99,9%", "99,95%", "99,99%"],
      ["Support", "1 werkdag", "Zelfde werkdag", "Toegewijd account-team"],
    ]},
  ],
};

const FAQ_EN = [
  { q: "Why don't you show fixed prices?", a: "Pricing scales with the number of properties, rooms and workflows you adopt. We price fairly — but honestly, one flat number would either be wrong for a 24-room boutique or wrong for a 400-room resort. A 5-minute call gives you an exact quote." },
  { q: "Can we start with a few workflows and add the rest later?", a: "Yes — that's how most hotels adopt BHT One. Start with Reception and Housekeeping, prove the value with your team, then bring in F&B, Marketing and the rest at your pace." },
  { q: "Is there a setup fee?", a: "Enterprise setups typically include a one-time onboarding and training package. Starter and Professional properties can self-onboard in a week with our guided setup and dedicated hospitality support." },
  { q: "How long is the contract?", a: "Monthly and annual terms available. Annual comes with a meaningful discount and a lower per-property price. No multi-year lock-in required." },
  { q: "What happens to our data if we leave?", a: "You own your data. On request we export everything — reservations, guests, knowledge base, communication history — in an open, portable format. No hostage tactics." },
];

const FAQ_NL = [
  { q: "Waarom tonen jullie geen vaste prijzen?", a: "Prijzen schalen met het aantal properties, kamers en workflows die u adopteert. We prijzen eerlijk — maar één plat bedrag zou óf te hoog zijn voor een boutique van 24 kamers óf te laag voor een resort van 400 kamers. Een gesprek van 5 minuten levert u een exacte quote." },
  { q: "Kunnen we starten met een paar workflows en de rest later toevoegen?", a: "Ja — zo adopteren de meeste hotels BHT One. Start met Receptie en Housekeeping, laat uw team de waarde ervaren, en breng daarna F&B, Marketing en de rest in uw eigen tempo." },
  { q: "Is er een setup fee?", a: "Enterprise-setups bevatten meestal een eenmalig onboarding- en trainingspakket. Starter- en Professional-properties kunnen binnen een week zelf onboarden met begeleide setup en hospitality-support." },
  { q: "Hoe lang is het contract?", a: "Maandelijkse en jaarlijkse termijnen beschikbaar. Jaarlijks komt met een aantrekkelijke korting en een lagere per-property-prijs. Geen meerjarige lock-in vereist." },
  { q: "Wat gebeurt er met onze data als we stoppen?", a: "U bent eigenaar van uw data. Op verzoek exporteren we alles — reserveringen, gasten, kennisbank, communicatiehistorie — in een open, portable format. Geen gijzelingstactieken." },
];

export default function Pricing() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const tiers = lang === "nl" ? TIERS_NL : TIERS_EN;
  const matrix = MATRIX[lang];
  const faq = lang === "nl" ? FAQ_NL : FAQ_EN;

  const cell = (v) => {
    if (v === true) return <Check size={16} weight="bold" className="text-bht-gold" />;
    if (v === false) return <Minus size={14} className="text-white/25" />;
    return <span className="text-xs text-white/80">{v}</span>;
  };

  return (
    <>
      <SEO
        title={lang === "nl" ? "Prijzen — BHT One" : "Pricing — BHT One"}
        path="/pricing"
        description={lang === "nl"
          ? "Drie transparante plans die met uw hotel meegroeien. Vraag een quote op maat aan."
          : "Three transparent plans that grow with your hotel. Request pricing tailored to your property."}
      />

      {/* HERO */}
      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Eyebrow>{lang === "nl" ? "Prijzen" : "Pricing"}</Eyebrow>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-4xl font-serif text-5xl leading-[1.02] tracking-tight sm:text-6xl md:text-7xl"
          >
            {lang === "nl" ? "Prijzen die met uw hotel meegroeien." : "Pricing that grows with your hotel."}
          </motion.h1>
          <p className="mt-8 max-w-2xl text-lg text-white/65">
            {lang === "nl"
              ? "Drie plans. Geen verborgen fees. Geen per-integratie-toeslagen. Voor uw exacte prijs — gebaseerd op uw properties, kamers en workflows — vraagt u een quote aan; we reageren binnen één werkdag."
              : "Three plans. No hidden fees. No per-integration surcharges. For your exact price — based on your properties, rooms and workflows — request a quote and we'll reply within one business day."}
          </p>
        </div>
      </section>

      {/* TIER CARDS */}
      <section className="relative border-y border-white/[0.06] py-16" data-testid="pricing-tiers">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid gap-6 lg:grid-cols-3">
            {tiers.map((tier, i) => (
              <motion.div
                key={tier.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className={`relative flex flex-col rounded-2xl border p-8 ${
                  tier.popular
                    ? "border-bht-gold/40 bg-gradient-to-b from-bht-gold/[0.08] to-transparent gold-shadow"
                    : "border-white/[0.06] bg-white/[0.02]"
                }`}
                data-testid={`pricing-tier-${tier.key}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-8 rounded-full bg-bht-gold px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-bht-void">
                    {lang === "nl" ? "Meest gekozen" : "Most chosen"}
                  </div>
                )}
                <div className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">T/{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-4 font-serif text-3xl leading-tight">{tier.name}</div>
                <p className="mt-3 text-sm text-white/60">{tier.tagline}</p>
                <div className="mt-6 rounded-lg border border-white/[0.06] bg-black/40 px-4 py-3 text-xs text-white/70">
                  {tier.ideal}
                </div>
                <ul className="mt-6 flex-1 space-y-2.5">
                  {tier.highlights.map((h, j) => (
                    <li key={j} className={`flex items-start gap-2 text-sm ${h.endsWith("plus:") ? "font-mono text-[11px] uppercase tracking-widest text-white/50" : "text-white/80"}`}>
                      {!h.endsWith("plus:") && <Check size={14} weight="bold" className="mt-1 shrink-0 text-bht-gold" />}
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <GoldButton
                    to={`/${lang}/book-demo`}
                    className="w-full justify-center"
                    testId={`pricing-cta-${tier.key}`}
                  >
                    {lang === "nl" ? "Vraag prijs aan" : "Request pricing"}
                  </GoldButton>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE MATRIX */}
      <section className="relative py-24 lg:py-32" data-testid="pricing-matrix">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading
            eyebrow={lang === "nl" ? "Feature-vergelijking" : "Feature comparison"}
            title={lang === "nl" ? "Wat u krijgt in elk plan." : "What's in every plan."}
          />
          <div className="mt-12 overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02]">
            {/* Header row */}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-4 border-b border-white/[0.06] bg-black/40 px-6 py-4 text-[11px] font-mono uppercase tracking-widest text-white/50">
              <div>{lang === "nl" ? "Feature" : "Feature"}</div>
              <div className="text-center">Starter</div>
              <div className="text-center text-bht-gold">Professional</div>
              <div className="text-center">Enterprise</div>
            </div>
            {matrix.map((section) => (
              <div key={section.section}>
                <div className="border-b border-white/[0.04] bg-white/[0.02] px-6 py-2.5 font-mono text-[10px] uppercase tracking-widest text-bht-gold/80">
                  {section.section}
                </div>
                {section.rows.map(([feature, s, p, e], i) => (
                  <div key={i} className="grid grid-cols-[1.4fr_1fr_1fr_1fr] items-center gap-4 border-b border-white/[0.04] px-6 py-3 last:border-b-0">
                    <div className="text-sm text-white/85">{feature}</div>
                    <div className="flex justify-center">{cell(s)}</div>
                    <div className="flex justify-center">{cell(p)}</div>
                    <div className="flex justify-center">{cell(e)}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative border-t border-white/[0.06] py-24" data-testid="pricing-faq">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <SectionHeading eyebrow="FAQ" title={lang === "nl" ? "Vragen die hoteliers ons stellen" : "Questions hoteliers ask us"} />
          <div className="mt-12 space-y-3">
            {faq.map((f, i) => (
              <details key={i} className="group rounded-xl border border-white/[0.06] bg-white/[0.02] px-5 py-4 open:bg-white/[0.03]" data-testid={`pricing-faq-${i}`}>
                <summary className="cursor-pointer list-none font-serif text-lg text-white [&::-webkit-details-marker]:hidden">
                  <span className="mr-3 inline-block w-6 font-mono text-[11px] tracking-widest text-bht-gold">Q/{String(i + 1).padStart(2, "0")}</span>
                  {f.q}
                </summary>
                <p className="mt-3 pl-8 text-sm leading-relaxed text-white/60">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative border-t border-white/[0.06] py-32">
        <div className="mx-auto max-w-3xl px-6 text-center lg:px-10">
          <SectionHeading
            align="center"
            eyebrow={lang === "nl" ? "Klaar voor uw quote?" : "Ready for your quote?"}
            title={lang === "nl" ? "Vraag prijzen aan die passen bij uw hotel." : "Request pricing tailored to your hotel."}
            sub={lang === "nl"
              ? "Een hotelier van ons team maakt een quote op basis van uw properties, kamers en workflows. Antwoord binnen één werkdag."
              : "A hotelier from our team will scope pricing to your properties, rooms and workflows. Reply within one business day."}
          />
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <GoldButton to={`/${lang}/book-demo`}>{lang === "nl" ? "Vraag prijs aan" : "Request pricing"}</GoldButton>
            <GhostButton to={`/${lang}/contact`}>{t("nav.contact")} →</GhostButton>
          </div>
        </div>
      </section>
    </>
  );
}
